import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BricsCampaignModal from "./BricsCampaignModal";
import mainPosterImg from "../../../assets/images/brics_campaign_main_poster.jpg";
import energyPosterImg from "../../../assets/images/brics_campaign_energy_poster.jpg";
import {
  FaCloudUploadAlt,
  FaCalendarAlt,
  FaLeaf,
  FaArrowRight,
  FaExpand,
} from "react-icons/fa";
import "./BricsHeroSection.css";


export default function BricsHeroSection() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleUploadClick = () => {
    navigate("/brics-sustainable-lifestyle-week");
  };
  const [selectedPoster, setSelectedPoster] = useState("main"); // 'main' | 'energy'

  const activePosterSrc = selectedPoster === "main" ? mainPosterImg : energyPosterImg;
  const activePosterAlt =
    selectedPoster === "main"
      ? "BRICS Sustainable Lifestyle Week 2026 - One Week, Seven Actions, One Planet"
      : "Adopt Mission LiFE Save Energy - 24°C A Small Change That Saves";

  return (
    <>
      <section className="brics-ref-hero" aria-labelledby="brics-ref-hero-title">
        {/* Subtle Dotted Pattern Overlay */}
        <div className="brics-ref-hero__dot-pattern" aria-hidden="true" />

        {/* Floating Decorative Sustainability Outlines */}
        <div className="brics-ref-hero__decorations" aria-hidden="true">
          {/* 1. Save Energy (Lightbulb) */}
          <svg className="brics-decor brics-decor--energy" viewBox="0 0 64 64" fill="none" title="Save Energy">
            <path
              d="M32 10C22 10 14 18 14 28C14 34 18 39 21 43C23 45 24 47 24 50H40C40 47 41 45 43 43C46 39 50 34 50 28C50 18 42 10 32 10Z"
              stroke="#7dd3fc"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M26 50V54H38V50" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M29 57H35" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="32" y1="2" x2="32" y2="6" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="12" y1="12" x2="15" y2="15" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="52" y1="12" x2="49" y2="15" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
          </svg>

          {/* 2. Save Water (Water Droplet & Ripple) */}
          <svg className="brics-decor brics-decor--water" viewBox="0 0 64 64" fill="none" title="Save Water">
            <path
              d="M32 8 C32 8 16 28 16 39 C16 48 23 55 32 55 C41 55 48 48 48 39 C48 28 32 8 32 8 Z"
              stroke="#7dd3fc"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M24 40 C24 44 27 47 31 48"
              stroke="#7dd3fc"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          {/* 3. Adopt Healthy Lifestyles (Bicycle) */}
          <svg className="brics-decor brics-decor--lifestyle" viewBox="0 0 64 64" fill="none" title="Adopt Healthy Lifestyles">
            <circle cx="18" cy="42" r="11" stroke="#7dd3fc" strokeWidth="2.2" />
            <circle cx="46" cy="42" r="11" stroke="#7dd3fc" strokeWidth="2.2" />
            <path
              d="M18 42 L29 27 L43 27 M18 42 L32 42 L26 23 M32 42 L29 27 M46 42 L42 20 M38 20 H46"
              stroke="#7dd3fc"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M23 23 H29" stroke="#7dd3fc" strokeWidth="2.5" strokeLinecap="round" />
          </svg>

          {/* 4. Adopt Sustainable Food Systems (Apple with Leaf) */}
          <svg className="brics-decor brics-decor--food" viewBox="0 0 64 64" fill="none" title="Adopt Sustainable Food Systems">
            <path d="M32 18 C33 12 37 8 42 8" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M34 14 C38 12 43 14 44 17 C41 18 36 17 34 14 Z" stroke="#7dd3fc" strokeWidth="2" strokeLinejoin="round" />
            <path
              d="M32 24 C27 18 14 18 14 32 C14 46 25 54 32 54 C39 54 50 46 50 32 C50 18 37 18 32 24 Z"
              stroke="#7dd3fc"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* 5. Say No to Single-Use Plastics (Bottle with Ban) */}
          <svg className="brics-decor brics-decor--plastic" viewBox="0 0 64 64" fill="none" title="Say No to Single-Use Plastics">
            <circle cx="32" cy="32" r="22" stroke="#7dd3fc" strokeWidth="2.2" />
            <line x1="16" y1="16" x2="48" y2="48" stroke="#7dd3fc" strokeWidth="2.2" strokeLinecap="round" />
            <path
              d="M28 20 V17 H36 V20 L38 25 V43 C38 45 36 46 34 46 H30 C28 46 26 45 26 43 V25 Z"
              stroke="#7dd3fc"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.8"
            />
          </svg>

          {/* 6. Reduce Waste (Recycling Symbol) */}
          <svg className="brics-decor brics-decor--waste" viewBox="0 0 64 64" fill="none" title="Reduce Waste">
            <path
              d="M32 10L37 18H27L32 10ZM37 18H48C51 18 53 20 54 22L46 36L42 34M16 46C15 44 15 42 16 40L21 31L25 33L20 42H38M20 42L15 50L10 42H20Z"
              stroke="#7dd3fc"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* 7. Reduce E-Waste (Microchip / Electronic Circuit) */}
          <svg className="brics-decor brics-decor--ewaste" viewBox="0 0 64 64" fill="none" title="Reduce E-Waste">
            <rect x="20" y="20" width="24" height="24" rx="4" stroke="#7dd3fc" strokeWidth="2.2" />
            <rect x="26" y="26" width="12" height="12" rx="2" stroke="#7dd3fc" strokeWidth="1.6" />
            <line x1="26" y1="13" x2="26" y2="20" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="32" y1="13" x2="32" y2="20" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="38" y1="13" x2="38" y2="20" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="26" y1="44" x2="26" y2="51" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="32" y1="44" x2="32" y2="51" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="38" y1="44" x2="38" y2="51" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="13" y1="26" x2="20" y2="26" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="13" y1="32" x2="20" y2="32" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="13" y1="38" x2="20" y2="38" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="44" y1="26" x2="51" y2="26" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="44" y1="32" x2="51" y2="32" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
            <line x1="44" y1="38" x2="51" y2="38" stroke="#7dd3fc" strokeWidth="2" strokeLinecap="round" />
          </svg>

          <div className="brics-decor-landscape-left">
            <svg viewBox="0 0 320 180" fill="none" className="brics-decor-landscape-svg">
              {/* Back Hill */}
              <path d="M-20 180 Q60 90 180 130 T340 180 Z" fill="#bbf7d0" opacity="0.45" />
              {/* Front Hill */}
              <path d="M-30 180 Q80 110 220 150 T360 180 Z" fill="#86efac" opacity="0.55" />
              {/* Windmill 1 (Tall) */}
              <g stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" opacity="0.85">
                <line x1="60" y1="70" x2="62" y2="155" />
                <circle cx="60" cy="70" r="3" fill="#38bdf8" />
                <line x1="60" y1="70" x2="60" y2="42" />
                <line x1="60" y1="70" x2="84" y2="84" />
                <line x1="60" y1="70" x2="36" y2="84" />
              </g>
              {/* Windmill 2 (Smaller) */}
              <g stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" opacity="0.75">
                <line x1="110" y1="95" x2="111" y2="160" />
                <circle cx="110" cy="95" r="2.5" fill="#38bdf8" />
                <line x1="110" y1="95" x2="110" y2="72" />
                <line x1="110" y1="95" x2="130" y2="107" />
                <line x1="110" y1="95" x2="90" y2="107" />
              </g>
            </svg>
          </div>

          {/* Bottom-Right Landscape (Solar Panel & Leaf Branch) */}
          <div className="brics-decor-landscape-right">
            <svg viewBox="0 0 240 160" fill="none" className="brics-decor-landscape-svg">
              {/* Leaf branch */}
              <g fill="#4ade80" opacity="0.6">
                <path d="M190 140 Q215 90 240 60 Q235 90 210 130 Z" />
                <path d="M175 145 Q190 115 220 100 Q205 125 185 145 Z" />
                <path d="M160 150 Q165 120 190 110 Q175 135 165 152 Z" />
              </g>
              {/* Tilted Solar Panel */}
              <g transform="translate(110, 85)">
                <line x1="10" y1="35" x2="10" y2="50" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="50" y1="30" x2="50" y2="50" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" />
                <polygon points="0,35 20,5 75,5 55,35" fill="#0284c7" stroke="#38bdf8" strokeWidth="2" />
                <line x1="7" y1="20" x2="62" y2="20" stroke="#bae6fd" strokeWidth="1.2" />
                <line x1="28" y1="5" x2="18" y2="35" stroke="#bae6fd" strokeWidth="1.2" />
                <line x1="47" y1="5" x2="37" y2="35" stroke="#bae6fd" strokeWidth="1.2" />
              </g>
            </svg>
          </div>
        </div>

        <div className="brics-ref-hero__inner">
          {/* Left Column: Original Campaign Poster Frame & Switcher Tabs */}
          <div className="brics-ref-hero__art">
            <div
              className="brics-ref-poster-card"
              onClick={handleUploadClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleUploadClick()}
              title="Click to view details and upload participation"
            >
              <img
                src={activePosterSrc}
                alt={activePosterAlt}
                className="brics-ref-poster-card__img"
              />
              <div className="brics-ref-poster-card__hover-badge">
                <FaExpand style={{ marginRight: "6px" }} /> Click to View Details &amp; Upload
              </div>
            </div>

            {/* Campaign Posters Switcher Tabs under poster */}
            <div className="brics-ref-tabs-container brics-ref-tabs-container--under-poster">
              <span className="brics-ref-tabs-label">Campaign Posters:</span>
              <div className="brics-ref-tabs-pill">
                <button
                  type="button"
                  className={`brics-ref-tab-btn ${selectedPoster === "main" ? "is-active" : ""}`}
                  onClick={() => setSelectedPoster("main")}
                >
                  7 Actions Poster
                </button>
                <button
                  type="button"
                  className={`brics-ref-tab-btn ${selectedPoster === "energy" ? "is-active" : ""}`}
                  onClick={() => setSelectedPoster("energy")}
                >
                  24°C Save Energy Poster
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Headings, CTA Button & Information */}
          <div className="brics-ref-hero__copy">
            {/* Top Pill Badges */}
            <div className="brics-ref-hero__pills">
              <span className="brics-ref-pill brics-ref-pill--mission">
                <span className="brics-ref-pill__dot" aria-hidden="true" />
                <span>BRICS INDIA 2026 • MISSION LiFE</span>
              </span>
              <span className="brics-ref-pill brics-ref-pill--date">
                <FaCalendarAlt className="brics-ref-pill__cal-icon" />
                <span>Nov 1st – 7th, 2026</span>
              </span>
            </div>

            {/* Campaign Headline */}
            <h2 id="brics-ref-hero-title" className="brics-ref-hero__heading">
              <span className="brics-ref-hero__heading-blue">BRICS Sustainable</span>
              <span className="brics-ref-hero__heading-blue">Lifestyle Week 2026</span>
            </h2>

            {/* Subtitle & Tagline */}
            <div className="brics-ref-hero__taglines">
              <h3 className="brics-ref-hero__tagline-bold">ONE WEEK, SEVEN ACTIONS, ONE PLANET.</h3>
              <p className="brics-ref-hero__tagline-sub">Let's pledge to adopt sustainable lifestyles</p>
            </div>

            {/* Upload Button */}
            <div className="brics-ref-hero__actions">
              <button
                type="button"
                className="brics-ref-upload-cta"
                onClick={handleUploadClick}
                title="Click to open BRICS Sustainable Lifestyle Week participation portal"
              >
                <span className="brics-ref-upload-cta__icon">
                  <FaCloudUploadAlt />
                </span>
                <span className="brics-ref-upload-cta__label">Upload Participation Details</span>
                <span className="brics-ref-upload-cta__arrow" aria-hidden="true">
                  <FaArrowRight />
                </span>
              </button>
            </div>

            {/* Supporting Note */}
            <div className="brics-ref-hero__note">
              <FaLeaf className="brics-ref-hero__leaf-icon" />
              <span>Report activities, student counts &amp; geotagged photos</span>
            </div>
          </div>
        </div>
      </section>

      {/* The existing BRICS SLW Pop-up Modal */}
      <BricsCampaignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCompleted={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
}
